import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useLocalization from "../../hooks/useLocalization";

const LocationsList = ({
  locations,
  expandedLocationId,
  onExpandToggle,
  onSelectLocation,
  selectedLocationId,
  onHoverLocation,
}) => {
  const { getLabel } = useLocalization();

  if (!locations || locations.length === 0) {
    return (
      <Typography
        color="textSecondary"
        sx={{ textAlign: "center", py: 4 }}
      >
        {getLabel("scheduledLocationsEmpty")}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: "bold" }}
      >
        {getLabel("scheduledLocations")} ({locations.length})
      </Typography>

      <List sx={{ width: "100%", p: 0 }}>
        {locations.map((location) => {
          const isExpanded = expandedLocationId === location.id;
          const isSelected = selectedLocationId === location.id;

          return (
            <Card
              key={location.id}
              sx={{
                mb: 1.5,
                border:
                  isSelected && !isExpanded
                    ? "2px solid #1976d2"
                    : "1px solid #e0e0e0",
                backgroundColor:
                  isSelected && !isExpanded ? "#f5f5f5" : "transparent",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: 1,
                },
              }}
              onMouseEnter={() => onHoverLocation?.(location.id)}
              onMouseLeave={() => onHoverLocation?.(null)}
              onClick={() => {
                onSelectLocation(location.id);
                if (!isExpanded) {
                  onExpandToggle(location.id);
                }
              }}
            >
              <CardHeader
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", flex: 1 }}
                    >
                      {location.name}
                    </Typography>
                    <Chip
                      label={`${location.calls.length} ${
                        location.calls.length === 1
                          ? getLabel("call")
                          : getLabel("calls")
                      }`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                }
                subheader={
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="caption"
                      display="block"
                    >
                      #{location.number}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                    >
                      {location.address}
                    </Typography>
                  </Box>
                }
                action={
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpandToggle(isExpanded ? null : location.id);
                    }}
                    sx={{
                      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                }
                sx={{ pb: 0 }}
              />

              <Collapse
                in={isExpanded}
                timeout="auto"
                unmountOnExit
              >
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    {getLabel("scheduledCalls")}
                  </Typography>
                  <List sx={{ p: 0 }}>
                    {location.calls.map((call, idx) => (
                      <ListItem
                        key={idx}
                        sx={{
                          py: 1,
                          px: 0,
                          borderLeft: "3px solid #1976d2",
                          pl: 1.5,
                          backgroundColor: "#fafafa",
                          mb: 0.5,
                        }}
                      >
                        <ListItemText
                          primary={call.projectName}
                          secondary={
                            <Typography
                              component="span"
                              variant="caption"
                              color="textSecondary"
                            >
                              {call.scheduleDate} • {call.status}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Collapse>
            </Card>
          );
        })}
      </List>
    </Box>
  );
};

export default LocationsList;
